import { SvgIcon } from '@mui/material';
import type { Variants } from 'motion/react';
import { motion, useAnimation } from 'motion/react';
import { forwardRef, useImperativeHandle } from 'react';

import type { AnimatedIconProps, AnimeatableIconRef } from './types';

const groupVariants: Variants = {
  visible: {
    rotate: 0,
    y: 0,
    x: 0,
  },
  animate: {
    rotate: [0, -20, -20, 15, 15, 0],
    y: [0, -0.5, -0.5, 0, 0, 0],
    x: [0, 0, 0, 0.5, 0.5, 0],
    transition: {
      duration: 1.8,
      ease: 'easeInOut',
    },
  },
};

const blinkVariants: Variants = {
  visible: {
    opacity: 1,
  },
  animate: {
    opacity: [1, 0, 1, 0, 1, 0, 1],
    transition: {
      duration: 1.8,
      ease: 'easeInOut',
    },
  },
};

const CameraIcon = forwardRef<AnimeatableIconRef, AnimatedIconProps>(
  ({ size = 28, strokeWidth = 2, titleAccess, color }, ref) => {
    const controls = useAnimation();

    useImperativeHandle(ref, () => ({
      startAnimation: () => controls.start('animate'),
      stopAnimation: () => controls.start('visible'),
    }));

    return (
      <SvgIcon
        titleAccess={titleAccess}
        color={color}
        sx={{ width: size, height: size }}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <motion.g
            variants={groupVariants}
            initial="visible"
            animate={controls}
          >
            <path d="M16.75 12h3.632a1 1 0 0 1 .894 1.447l-2.034 4.069a1 1 0 0 1-1.708.134l-2.124-2.97" />
            <path d="M17.106 9.053a1 1 0 0 1 .447 1.341l-3.106 6.211a1 1 0 0 1-1.342.447L3.61 12.3a2.92 2.92 0 0 1-1.3-3.91L3.69 5.6a2.92 2.92 0 0 1 3.92-1.3z" />
            <motion.path
              d="M7 9h.01"
              variants={blinkVariants}
              animate={controls}
            />
          </motion.g>
          <path d="M2 19h3.76a2 2 0 0 0 1.8-1.1L9 15" />
          <path d="M2 21v-4" />
        </svg>
      </SvgIcon>
    );
  }
);

CameraIcon.displayName = 'CameraIcon';

export { CameraIcon };
